/**
 * In-app diagnostic log buffer — PERSISTED across reloads.
 *
 * The resident-app runs on locked kiosk tablets where adb / DevTools are usually
 * unavailable, so `console` output is invisible. This module keeps the last N log
 * lines in a ring buffer (mirroring console.log/warn/error) plus explicit
 * structured events (logEvent), and exposes a store the DebugOverlay reads.
 *
 * CRITICAL: the buffer is mirrored to localStorage and restored on boot, so it
 * survives full page reloads. That is what makes a fast reload/provision LOOP
 * diagnosable — the history accumulates across every reload instead of vanishing.
 * It is dependency-free and never throws into the console it wraps.
 */

export type DebugLevel = 'log' | 'warn' | 'error' | 'event';

export interface DebugEntry {
  id: number;
  t: number; // epoch ms
  level: DebugLevel;
  msg: string;
}

const MAX = 300;
const STORAGE_KEY = 'caro_debug_log';
let seq = 0;
let buffer: DebugEntry[] = restore();
const listeners = new Set<() => void>();

function restore(): DebugEntry[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as DebugEntry[];
    if (Array.isArray(arr)) {
      seq = arr.reduce((m, e) => Math.max(m, e.id || 0), 0);
      return arr.slice(-MAX);
    }
  } catch {
    /* corrupt / unavailable — start fresh */
  }
  return [];
}

let persistTimer: number | null = null;
function persistNow() {
  persistTimer = null;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  } catch {
    /* quota / unavailable */
  }
}
function schedulePersist() {
  if (persistTimer != null) return;
  try {
    persistTimer = window.setTimeout(persistNow, 300);
  } catch {
    /* no window.setTimeout — ignore */
  }
}

function notify() {
  listeners.forEach((l) => l());
}

function add(level: DebugLevel, msg: string) {
  const clipped = msg.length > 600 ? `${msg.slice(0, 600)}…` : msg;
  buffer = [...buffer, { id: ++seq, t: Date.now(), level, msg: clipped }];
  if (buffer.length > MAX) buffer = buffer.slice(-MAX);
  schedulePersist();
  notify();
}

function fmt(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return a.message;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(' ');
}

let installed = false;

/** Wrap console.log/warn/error so their output is also captured + persisted. */
export function installDebugLog() {
  if (installed || typeof console === 'undefined') return;
  installed = true;
  (['log', 'warn', 'error'] as const).forEach((level) => {
    const orig = console[level] ? console[level].bind(console) : () => {};
    console[level] = (...args: unknown[]) => {
      orig(...args);
      try {
        add(level, fmt(args));
      } catch {
        /* never break the real console */
      }
    };
  });
  // Flush right before a reload so the last lines of a loop cycle aren't lost.
  try {
    window.addEventListener('beforeunload', persistNow);
  } catch {
    /* no window */
  }
  // A per-load marker makes reload cycles visible in the persisted history.
  add('event', `━━━ page load ${new Date().toLocaleTimeString([], { hour12: false })} ${location.pathname} ━━━`);
}

/** Record an explicit structured event (used by provisioning/auth flows). */
export function logEvent(tag: string, data?: unknown) {
  add('event', data === undefined ? `[${tag}]` : `[${tag}] ${fmt([data])}`);
}

export function clearDebugLog() {
  buffer = [];
  persistNow();
  notify();
}

// ── Store API (for useSyncExternalStore) ─────────────────────────
export function subscribeDebugLog(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function getDebugLog(): DebugEntry[] {
  return buffer;
}

// ── Debug UI visibility store ─────────────────────────────────────
// The overlay is HIDDEN by default (residents never see it) and is toggled
// remotely per-device via the MQTT `debug_overlay` command. State is persisted
// so it survives reloads. `open` drives whether the full panel is expanded.

export interface DebugUiState {
  enabled: boolean;
  open: boolean;
}

const UI_KEY = 'caro_debug_ui';
const uiListeners = new Set<() => void>();

let ui: DebugUiState = loadUi();

function loadUi(): DebugUiState {
  try {
    const raw = window.localStorage.getItem(UI_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<DebugUiState>;
      return { enabled: Boolean(p.enabled), open: Boolean(p.open) };
    }
  } catch {
    /* unavailable */
  }
  return { enabled: false, open: false };
}

function saveUi() {
  try {
    window.localStorage.setItem(UI_KEY, JSON.stringify(ui));
  } catch {
    /* quota / unavailable */
  }
}

/** Merge a patch into the UI state, persist, and notify subscribers. */
export function setDebugUi(patch: Partial<DebugUiState>) {
  ui = { ...ui, ...patch };
  saveUi();
  uiListeners.forEach((l) => l());
  logEvent('debug.ui', ui);
}

export function subscribeDebugUi(cb: () => void) {
  uiListeners.add(cb);
  return () => {
    uiListeners.delete(cb);
  };
}

export function getDebugUi(): DebugUiState {
  return ui;
}
